import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

// The actual product images continue to come from Shopify's selected variant.
// Only the illustrative engraving card takes on a matching metal tone.
const metalTones = new Set(['gold', 'silver', 'rose-gold', 'copper', 'bronze', 'black', 'gray']);
function updateEngravingTone(picker) {
  if (!picker) return;
  const selected = [...picker.querySelectorAll('input[data-med-tone]:checked')].find(input => metalTones.has(input.dataset.medTone) && !/secondary|symbol|logo/i.test(input.name));
  for (const editor of document.querySelectorAll('med-personalizer')) {
    if (editor.dataset.productId !== picker.dataset.productId) continue;
    const preview = editor.querySelector('.med-text-preview');
    if (!preview) continue;
    const tone = selected?.dataset.medTone || editor.dataset.initialTone || 'silver';
    preview.dataset.medTone = tone;
    editor.updateAccessoryTone?.(tone);
  }
}
document.addEventListener('change', event => {
  if (event.target.matches('variant-picker input')) updateEngravingTone(event.target.closest('variant-picker'));
});
document.addEventListener('variant:update', () => queueMicrotask(() => document.querySelectorAll('variant-picker').forEach(updateEngravingTone)));
document.querySelectorAll('variant-picker').forEach(updateEngravingTone);

class MedicalPriceSlider extends HTMLElement {
  static observedAttributes = ['data-range-max', 'data-min-value', 'data-max-value'];
  connectedCallback() {
    if (this.attached) return;
    this.attached = true;
    this.addEventListener('input', this.onRangeInput);
    this.addEventListener('change', this.onRangeChange);
    this.addEventListener('keydown', this.onRangeKey);
    this.priceFacet = this.closest('price-facet-component');
    this.priceFacet?.addEventListener('change', this.onTextChange);
    this.syncFromAttributes();
  }
  disconnectedCallback() {
    this.removeEventListener('input', this.onRangeInput);
    this.removeEventListener('change', this.onRangeChange);
    this.removeEventListener('keydown', this.onRangeKey);
    this.priceFacet?.removeEventListener('change', this.onTextChange);
    this.attached = false;
  }
  attributeChangedCallback() { queueMicrotask(() => { if (this.isConnected) this.syncFromAttributes(); }); }
  get minRange() { return this.querySelector('[data-range-min]'); }
  get maxRange() { return this.querySelector('[data-range-max]'); }
  get minText() { return this.closest('price-facet-component')?.querySelector('[ref="minInput"]'); }
  get maxText() { return this.closest('price-facet-component')?.querySelector('[ref="maxInput"]'); }
  get ceiling() { return Math.max(0, Number(this.dataset.rangeMax) || 0); }
  get currency() { return this.dataset.currency || 'USD'; }
  get moneyFormat() { return this.dataset.moneyFormat || '${{amount}}'; }
  get numberFormat() { return this.moneyFormat.match(/{{\s*\w+\s*}}/)?.[0] || '{{amount}}'; }
  syncFromAttributes() {
    if (!this.minRange || !this.maxRange) return;
    this.minRange.max = this.maxRange.max = this.ceiling;
    this.minRange.value = Math.min(this.ceiling, Math.max(0, Number(this.dataset.minValue) || 0));
    this.maxRange.value = Math.min(this.ceiling, Math.max(Number(this.minRange.value), Number(this.dataset.maxValue) || 0));
    this.paint();
  }
  paint() {
    const min = Number(this.minRange.value), max = Number(this.maxRange.value);
    this.style.setProperty('--med-range-start', `${this.ceiling ? min / this.ceiling * 100 : 0}%`);
    this.style.setProperty('--med-range-end', `${this.ceiling ? max / this.ceiling * 100 : 100}%`);
    this.querySelector('[data-min-label]').textContent = formatMoney(min, this.moneyFormat, this.currency);
    this.querySelector('[data-max-label]').textContent = formatMoney(max, this.moneyFormat, this.currency);
    this.minRange.setAttribute('aria-valuetext', formatMoney(min, this.moneyFormat, this.currency));
    this.maxRange.setAttribute('aria-valuetext', formatMoney(max, this.moneyFormat, this.currency));
    this.minRange.disabled = this.maxRange.disabled = this.ceiling === 0;
    // Keep the lower thumb reachable when both handles meet at the far end.
    this.minRange.style.zIndex = min === this.ceiling ? '3' : '1';
  }
  onRangeInput = event => {
    if (!event.target.matches('input[type="range"]')) return;
    const min = Number(this.minRange.value), max = Number(this.maxRange.value);
    if (min > max) {
      if (event.target === this.minRange) this.minRange.value = max;
      else this.maxRange.value = min;
    }
    this.paint();
    if (!this.minText || !this.maxText) return;
    const lower = Number(this.minRange.value), upper = Number(this.maxRange.value);
    this.minText.value = lower === 0 ? '' : formatMoney(lower, this.numberFormat, this.currency);
    this.maxText.value = upper === this.ceiling ? '' : formatMoney(upper, this.numberFormat, this.currency);
    this.minText.dataset.max = formatMoney(upper, this.numberFormat, this.currency);
    this.maxText.dataset.min = formatMoney(lower, this.numberFormat, this.currency);
  };
  onRangeChange = event => {
    if (!event.target.matches('input[type="range"]')) return;
    event.stopPropagation();
    this.onRangeInput(event);
    this.minText?.dispatchEvent(new Event('change', { bubbles: true }));
  };
  onRangeKey = event => { if (event.target.matches('input[type="range"]')) event.stopPropagation(); };
  onTextChange = event => {
    if (event.target !== this.minText && event.target !== this.maxText) return;
    this.minRange.value = this.minText.value.trim() ? Math.min(this.ceiling, Math.max(0, convertMoneyToMinorUnits(this.minText.value, this.currency) || 0)) : 0;
    this.maxRange.value = this.maxText.value.trim() ? Math.max(Number(this.minRange.value), Math.min(this.ceiling, convertMoneyToMinorUnits(this.maxText.value, this.currency) || 0)) : this.ceiling;
    this.paint();
  };
}

class MedicalNewestSort extends HTMLElement {
  connectedCallback() {
    if (this.attached) return;
    this.attached = true;
    this.addEventListener('change', event => {
      if (!event.target.matches('input[type="checkbox"]')) return;
      event.stopPropagation();
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      url.searchParams.set('sort_by', event.target.checked ? this.dataset.sortValue : this.dataset.defaultSort);
      const nativeForm = this.closest('facets-form-component');
      if (nativeForm?.updateFiltersByURL) nativeForm.updateFiltersByURL(url.toString());
      else window.location.assign(url.toString());
    });
  }
}

// Improve imported plain paragraphs without changing product facts, images, links, or rich content.
class MedicalProductDescription extends HTMLElement {
  connectedCallback() {
    if (this.enhanced) return;
    this.enhanced = true;
    const candidates = [...this.querySelectorAll('p, div')].filter(element =>
      element.childNodes.length && [...element.childNodes].every(child => child.nodeType === Node.TEXT_NODE || child.nodeName === 'BR'));
    if ([...this.childNodes].every(child => child.nodeType === Node.TEXT_NODE || child.nodeName === 'BR')) candidates.push(this);
    for (const element of candidates) this.enhancePlainText(element);
    for (const first of this.querySelectorAll('p')) {
      if (!first.parentNode) continue;
      const paragraphs = [];
      let cursor = first;
      while (cursor?.tagName === 'P' && !cursor.children.length && /^(Material|Materials|Size Width|Size Length|Width|Length|Chain length|Bracelet length|Plate size|Plate width|Dimensions|Weight|Fit|Finish|Adjustable length)\s*:\s*.+$/i.test(cursor.textContent.trim())) {
        paragraphs.push(cursor); cursor = cursor.nextElementSibling;
      }
      if (paragraphs.length < 2) continue;
      const combined = document.createElement('div');
      combined.textContent = paragraphs.map(p => p.textContent.trim()).join('\n');
      first.before(combined); paragraphs.forEach(p => p.remove()); this.enhancePlainText(combined);
    }
  }
  enhancePlainText(element) {
    const lines = [...element.childNodes].map(child => child.nodeName === 'BR' ? '\n' : child.textContent).join('').split(/\n+/).map(line => line.trim()).filter(Boolean);
    const spec = /^(Material|Materials|Size Width|Size Length|Width|Length|Chain length|Bracelet length|Plate size|Plate width|Dimensions|Weight|Fit|Finish|Adjustable length)\s*:\s*(.+)$/i;
    if (lines.filter(line => spec.test(line)).length < 2) return;
    const fragment = document.createDocumentFragment();
    let list = null;
    for (const line of lines) {
      const match = line.match(spec);
      if (match) {
        if (!list) { list = document.createElement('dl'); list.className = 'med-product-specs'; fragment.append(list); }
        const pair = document.createElement('div'), label = document.createElement('dt'), value = document.createElement('dd');
        label.textContent = match[1]; value.textContent = match[2]; pair.append(label, value); list.append(pair);
      } else {
        list = null;
        const heading = /^(Product info|Product information|Features|Sizing|Package includes|Care instructions)\s*:?$/i.test(line);
        const text = document.createElement(heading ? 'h3' : 'p'); text.textContent = line.replace(heading ? /:$/ : /$^/, ''); fragment.append(text);
      }
    }
    if (element === this) this.replaceChildren(fragment);
    else element.replaceWith(fragment);
  }
}

if (!customElements.get('med-price-slider')) customElements.define('med-price-slider', MedicalPriceSlider);
if (!customElements.get('med-newest-sort')) customElements.define('med-newest-sort', MedicalNewestSort);
if (!customElements.get('med-product-description')) customElements.define('med-product-description', MedicalProductDescription);
